import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";

const SCREENING_USAGE_COLLECTION = "screeningUsage";
const OPEN_SANCTIONS_UNIT_COST_USD = 0.1;
const SERPER_UNIT_COST_USD = 0.001;
const ADVERSE_MEDIA_SERPER_QUERIES_PER_NAME = 8;

function clean(value) {
  return String(value ?? "").trim();
}

function monthKeyFromDate(date = new Date()) {
  return date.toISOString().slice(0, 7);
}

function pickOrgId(form) {
  return clean(form?.orgId || form?.customer?.orgId || "");
}

function pickClientId(form) {
  return clean(form?.clientId || form?.id || form?.customer?.clientId || "");
}

function normalizeSubjects(subjects) {
  return (Array.isArray(subjects) ? subjects : [])
    .map((subject) => ({
      name: clean(subject?.name),
      roles: Array.isArray(subject?.roles) ? subject.roles.map(clean).filter(Boolean) : [],
    }))
    .filter((subject) => subject.name);
}

function roundCost(value) {
  const n = Number(value) || 0;
  return Math.round(n * 1_000_000) / 1_000_000;
}

function buildProviderCosts({ screeningType, namesCount, serperCredits }) {
  const type = clean(screeningType).toLowerCase();
  const count = Number(namesCount) || 0;

  if (type === "pep") {
    const estimatedCostUsd = roundCost(count * OPEN_SANCTIONS_UNIT_COST_USD);
    return {
      providers: {
        opensanctions: {
          calls: count,
          unitCostUsd: OPEN_SANCTIONS_UNIT_COST_USD,
          estimatedCostUsd,
        },
      },
      estimatedTotalCostUsd: estimatedCostUsd,
    };
  }

  if (type === "adverse_media") {
    const credits =
      Number(serperCredits) > 0
        ? Number(serperCredits)
        : count * ADVERSE_MEDIA_SERPER_QUERIES_PER_NAME;
    const estimatedCostUsd = roundCost(credits * SERPER_UNIT_COST_USD);
    return {
      providers: {
        serper: {
          credits,
          unitCostUsd: SERPER_UNIT_COST_USD,
          estimatedCostUsd,
          estimateBasis:
            Number(serperCredits) > 0
              ? "provided_serper_credits"
              : "estimated_8_queries_per_name",
        },
      },
      estimatedTotalCostUsd: estimatedCostUsd,
    };
  }

  if (type === "sanctions") {
    return {
      providers: {
        sanctions: {
          calls: count,
          unitCostUsd: 0,
          estimatedCostUsd: 0,
        },
      },
      estimatedTotalCostUsd: 0,
    };
  }

  return { providers: {}, estimatedTotalCostUsd: 0 };
}

export async function trackScreeningUsage({
  form,
  screeningType,
  namesCount,
  subjects,
  serperCredits,
  source = "kyc",
}) {
  const type = clean(screeningType).toLowerCase();
  const orgId = pickOrgId(form);
  const count = Number(namesCount) || normalizeSubjects(subjects).length || 0;

  if (!orgId || !type || count <= 0) return;

  const now = new Date();
  const user = auth.currentUser;
  const providerCosts = buildProviderCosts({
    screeningType: type,
    namesCount: count,
    serperCredits,
  });

  try {
    await addDoc(collection(db, SCREENING_USAGE_COLLECTION), {
      orgId,
      clientId: pickClientId(form),
      screeningType: type,
      namesCount: count,
      monthKey: monthKeyFromDate(now),
      source,
      subjects: normalizeSubjects(subjects),
      providers: providerCosts.providers,
      estimatedTotalCostUsd: providerCosts.estimatedTotalCostUsd,
      createdAt: serverTimestamp(),
      createdAtIso: now.toISOString(),
      createdByUid: user?.uid || "",
      createdByEmail: user?.email || "",
    });
  } catch (e) {
    console.warn("[screeningUsage] Failed to record screening usage", {
      screeningType: type,
      orgId,
      namesCount: count,
      error: e?.message || e,
    });
  }
}
